import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import {
    Button, DialogContent, Dialog, DialogActions, Grid, Stack,
    InputLabel, OutlinedInput, DialogTitle, Alert, CircularProgress, MenuItem, Select, FormControl
} from '@mui/material'

import * as Yup from 'yup'
import { Formik } from 'formik'
import { useSnackbar } from 'notistack'

import AnimateButton from 'components/@extended/AnimateButton'
import { useApiData } from 'services/hook/useApiData'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { EVENT_BANNER_UPDATE_INIT, EVENT_BANNER_UPDATE_SUCCESS } from 'models/events/banner.event'
import { bannerDetail, bannerEdit } from 'models/banner'
import { FormFieldError } from 'components/Form'
import { BANNER_VIEW_TYPES, BANNER_PAGE_TYPES, BANNER_STATUSES } from 'constants/banner'

export const BannerEditDialog = () => {
    const [bannerId, setBannerId] = useState(null)
    const eventbus = useEventBusContext()

    useEffect(() => {
        return eventbus.on(EVENT_BANNER_UPDATE_INIT, (id) => setBannerId(id))
    }, [eventbus])

    return (
        <Dialog open={bannerId != null} scroll={'body'} maxWidth="xs" fullWidth onClose={() => setBannerId(null)}>
            {bannerId && (
                <BannerEdit
                    bannerId={bannerId}
                    onClose={() => setBannerId(null)}
                    onSuccess={(data) => {
                        eventbus.fire(EVENT_BANNER_UPDATE_SUCCESS, data)
                        setBannerId(null)
                    }}
                />
            )}
        </Dialog>
    )
}

const validation = Yup.object().shape({
    name: Yup.string().max(255).required('Name is required'),
    description: Yup.string().nullable(),
    view_type: Yup.string().oneOf(BANNER_VIEW_TYPES.map((i) => i.value)).required('View type is required'),
    page_type: Yup.string().oneOf(BANNER_PAGE_TYPES.map((i) => i.value)).required('Page type is required'),
    status: Yup.string().oneOf(BANNER_STATUSES.map((i) => i.value)).required('Status is required')
})

const BannerEdit = ({ bannerId, onClose, onSuccess }) => {
    const { enqueueSnackbar } = useSnackbar()

    const [data, , loading, loadData] = useApiData({ data: {} }, {}, (params, setData) => {
        bannerDetail(bannerId)
            .then((res) => setData(res.data || res))
            .catch((res) => {
                enqueueSnackbar(res.message || 'Failed to load banner details', { variant: 'error', autoHideDuration: 2000 })
                setData({})
            })
    }, false)

    useEffect(() => {
        if (bannerId) loadData({})
    }, [bannerId])

    const onSubmit = async (values, { setErrors }) => {
        await bannerEdit(bannerId, values)
            .then((res) => {
                onSuccess(res.data)
                enqueueSnackbar(res.message || 'Banner updated successfully', { variant: 'success', autoHideDuration: 1500 })
            })
            .catch((res) => {
                if (res.errors) {
                    res.errors['submit'] = res.message
                    setErrors(res.errors)
                } else {
                    setErrors({ submit: res.message })
                }
                enqueueSnackbar(res.message || 'Failed to update banner', { variant: 'error', autoHideDuration: 1500 })
            })
    }

    if (loading) {
        return (
            <>
                <DialogTitle>Edit Banner</DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </DialogContent>
            </>
        )
    }

    return (
        <Formik
            initialValues={{
                name: data?.name ?? '',
                description: data?.description ?? '',
                view_type: data?.view_type ?? '',
                page_type: data?.page_type ?? '',
                status: data?.status ?? 'active'
            }}
            enableReinitialize
            validationSchema={validation}
            onSubmit={onSubmit}
        >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                <form noValidate onSubmit={handleSubmit}>
                    <DialogTitle>Edit Banner</DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={3}>
                            {errors.submit && (
                                <Grid item size={{ xs: 12 }}>
                                    <Alert severity="error">{errors.submit}</Alert>
                                </Grid>
                            )}

                            <Grid item size={{ xs: 12 }}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="banner-name">Name *</InputLabel>
                                    <OutlinedInput id="banner-name" name="name" value={values.name} onBlur={handleBlur} onChange={handleChange} fullWidth error={Boolean(touched.name && errors.name)} />
                                    <FormFieldError touched={touched.name} errors={errors.name} />
                                </Stack>
                            </Grid>

                            <Grid item size={{ xs: 12 }}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="banner-status">Status *</InputLabel>
                                    <FormControl fullWidth error={Boolean(touched.status && errors.status)}>
                                        <Select id="banner-status" name="status" value={values.status} onChange={handleChange} onBlur={handleBlur}>
                                            {BANNER_STATUSES.map((i) => (<MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                    <FormFieldError touched={touched.status} errors={errors.status} />
                                </Stack>
                            </Grid>

                            <Grid item size={{ xs: 12, md: 6 }}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="banner-view-type">View Type *</InputLabel>
                                    <FormControl fullWidth error={Boolean(touched.view_type && errors.view_type)}>
                                        <Select id="banner-view-type" name="view_type" value={values.view_type} onChange={handleChange} onBlur={handleBlur}>
                                            {BANNER_VIEW_TYPES.map((i) => (<MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                    <FormFieldError touched={touched.view_type} errors={errors.view_type} />
                                </Stack>
                            </Grid>

                            <Grid item size={{ xs: 12, md: 6 }}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="banner-page-type">Page Type *</InputLabel>
                                    <FormControl fullWidth error={Boolean(touched.page_type && errors.page_type)}>
                                        <Select id="banner-page-type" name="page_type" value={values.page_type} onChange={handleChange} onBlur={handleBlur}>
                                            {BANNER_PAGE_TYPES.map((i) => (<MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                    <FormFieldError touched={touched.page_type} errors={errors.page_type} />
                                </Stack>
                            </Grid>

                            <Grid item size={{ xs: 12 }}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="banner-description">Description</InputLabel>
                                    <OutlinedInput id="banner-description" name="description" value={values.description} onBlur={handleBlur} onChange={handleChange} fullWidth multiline rows={3} error={Boolean(touched.description && errors.description)} />
                                    <FormFieldError touched={touched.description} errors={errors.description} />
                                </Stack>
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions sx={{ p: 1.5 }}>
                        <AnimateButton>
                            <Button disabled={isSubmitting} onClick={onClose} variant="outlined" color="secondary">Cancel</Button>
                        </AnimateButton>
                        <AnimateButton>
                            <Button disabled={isSubmitting} type="submit" variant="contained" color="primary">
                                {isSubmitting ? 'Updating...' : 'Update'}
                            </Button>
                        </AnimateButton>
                    </DialogActions>
                </form>
            )}
        </Formik>
    )
}

BannerEdit.propTypes = { onClose: PropTypes.func, onSuccess: PropTypes.func, bannerId: PropTypes.number }

export default BannerEdit